import { useEffect, useState } from 'react';
import posthog from 'posthog-js';

const SESSION_ID_NOT_FOUND = 'Session ID not found';
const SESSION_URL_NOT_FOUND = 'Session URL not found';

interface PosthogSessionInfo {
  sessionId?: string;
  sessionUrl?: string;
}

export const getPosthogSessionInfo = () => {
  try {
    const sessionId = posthog.get_session_id();
    const apiHost = posthog.config?.api_host;

    if (sessionId && apiHost) {
      const baseUrl = apiHost || 'https://app.posthog.com';
      return {
        sessionId,
        sessionUrl: `${baseUrl}/replay/${sessionId}`,
      };
    }
  } catch (error) {
    // Silently fail if PostHog is not initialized or unavailable
  }
  return { sessionId: undefined, sessionUrl: undefined };
};

/**
 * Hook to safely extract PostHog session ID and replay URL
 * @returns Object containing sessionId and sessionUrl if available, undefined otherwise
 */
const usePosthogSessionInfo = (): PosthogSessionInfo => {
  const [sessionInfo, setSessionInfo] = useState<PosthogSessionInfo>({
    sessionId: SESSION_ID_NOT_FOUND,
    sessionUrl: SESSION_URL_NOT_FOUND,
  });

  useEffect(() => {
    let hasFoundSession = false;
    let interval: ReturnType<typeof setInterval>;
    let timeout: ReturnType<typeof setTimeout>;

    const updateSessionInfo = () => {
      // Stop polling once we've found a session
      if (hasFoundSession) return;

      const { sessionId, sessionUrl } = getPosthogSessionInfo();

      if (sessionId && sessionUrl) {
        setSessionInfo({ sessionId, sessionUrl });
        hasFoundSession = true;
        clearInterval(interval);
        clearTimeout(timeout);
      }
    };

    // Try to get session info immediately
    updateSessionInfo();

    // Poll for session info in case PostHog initializes later
    interval = setInterval(updateSessionInfo, 1000);

    // Stop polling after 10 seconds
    timeout = setTimeout(() => {
      clearInterval(interval);
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  return sessionInfo;
};

export default usePosthogSessionInfo;
